import type { SizeProp } from "@fortawesome/fontawesome-svg-core";
import {
  FontAwesomeIcon as FAIcon,
  type FontAwesomeIconProps as FAIconProps,
} from "@fortawesome/react-fontawesome";
import { getIcon, type IconName } from "@/lib/icons/registry";
import styles from "./FontAwesomeIcon.module.scss";

interface BaseIconProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  style?: React.CSSProperties;
  onClick?: (event?: React.MouseEvent) => void;
  spin?: boolean;
  "data-testid"?: string;
}

export interface SolidIconProps extends BaseIconProps {
  icon: IconName<"solid">;
}

export interface RegularIconProps extends BaseIconProps {
  icon: IconName<"regular">;
}

export interface LightIconProps extends BaseIconProps {
  icon: IconName<"light">;
}

type IconSize = NonNullable<BaseIconProps["size"]>;

const sizeMap: Record<IconSize, SizeProp> = {
  xs: "xs",
  sm: "sm",
  md: "1x",
  lg: "lg",
  xl: "xl",
};

/**
 * Internal render function shared by all icon components.
 */
function renderIcon(
  iconDef: ReturnType<typeof getIcon>,
  { size = "sm", className, style, onClick, spin, "data-testid": dataTestId }: BaseIconProps,
) {
  return (
    <FAIcon
      icon={iconDef}
      size={sizeMap[size]}
      className={`${styles.Icon} ${className ?? ""}`}
      style={style as FAIconProps["style"]}
      onClick={onClick}
      spin={spin}
      data-size={size}
      data-testid={dataTestId}
    />
  );
}

/**
 * Solid variant icon. TypeScript enforces icon exists in solid registry.
 *
 * @example
 * <SolidIcon icon="check" />
 * <SolidIcon icon="spinner" spin />
 */
export function SolidIcon({ icon, ...props }: SolidIconProps) {
  return renderIcon(getIcon(icon, "solid"), props);
}

/**
 * Regular (outline) variant icon. TypeScript enforces icon exists in regular registry.
 *
 * @example
 * <RegularIcon icon="house" />
 * <RegularIcon icon="search" size="lg" />
 */
export function RegularIcon({ icon, ...props }: RegularIconProps) {
  return renderIcon(getIcon(icon, "regular"), props);
}

/**
 * Light variant icon. TypeScript enforces icon exists in light registry.
 *
 * @example
 * <LightIcon icon="clipboard" />
 */
export function LightIcon({ icon, ...props }: LightIconProps) {
  return renderIcon(getIcon(icon, "light"), props);
}

SolidIcon.displayName = "SolidIcon";
RegularIcon.displayName = "RegularIcon";
LightIcon.displayName = "LightIcon";
